// Never serialize messages, stacks, SDK/Axios objects or arbitrary URLs.
// They can carry credentials and customer data. Only known categories leave here.
const ERROR_CODES = new Set([
  'Error', 'TypeError', 'RangeError', 'SyntaxError', 'ReferenceError', 'AbortError',
  'AxiosError', 'SessionChangedError', 'ERR_NETWORK', 'ERR_CANCELED', 'ECONNABORTED',
  'ETIMEDOUT', 'ERR_BAD_REQUEST', 'ERR_BAD_RESPONSE',
  'NotAllowedError', 'InvalidStateError', 'NotSupportedError', 'SecurityError',
  'NotAuthorizedException', 'UserNotConfirmedException', 'UserNotFoundException',
  'PasswordResetRequiredException', 'InvalidPasswordException', 'InvalidParameterException',
  'CodeMismatchException', 'ExpiredCodeException', 'LimitExceededException',
  'TooManyRequestsException', 'TooManyFailedAttemptsException', 'NetworkError',
]);
const ENDPOINTS = new Set([
  'signup', 'accessPublicForm', 'accessClient', 'accessFav', 'accessLandingPage',
  'accessLocalAuth', 'accessQueryData', 'accessRows', 'accessObjects', 'accessPages',
  'accessUsers', 'accessUser', 'accessUserClients', 'accessReservations', 'accessEvents',
  'public/reservationAvailability', 'public/reservationHold', 'public/reservationConfirm',
  'analytics/capture-proxy', 'analytics/public-capture',
]);
const METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);
const read = (value: unknown, key: string): unknown => {
  try { return value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined; }
  catch { return undefined; }
};

export const safeErrorCode = (error: unknown): string => {
  for (const value of [read(error, 'code'), read(error, 'name')]) {
    if (typeof value === 'string' && ERROR_CODES.has(value)) return value;
  }
  return 'UnknownError';
};

export const safeErrorDiagnostic = (error: unknown) => {
  const status = read(read(error, 'response'), 'status') ?? read(error, 'status');
  return {
    code: safeErrorCode(error),
    status: typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined,
  };
};

export const diagnosticEndpoint = (value: unknown): string => {
  if (typeof value !== 'string') return 'unknown';
  try {
    const segments = new URL(value, 'https://diagnostic.invalid').pathname.split('/').filter(Boolean);
    for (let i = 0; i < segments.length; i += 1) {
      const pair = `${segments[i]}/${segments[i + 1]}`;
      if (ENDPOINTS.has(pair)) return pair;
      if (ENDPOINTS.has(segments[i])) return segments[i];
    }
  } catch { /* Do not fall back to the untrusted input. */ }
  return 'unknown';
};

export const safeApiDiagnostic = (url: unknown, method: unknown, error: unknown) => {
  const status = read(read(error, 'response'), 'status');
  const verb = typeof method === 'string' ? method.toUpperCase() : '';
  return {
    url: diagnosticEndpoint(url),
    method: METHODS.has(verb) ? verb : 'UNKNOWN',
    status: typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined,
    code: safeErrorCode(error),
  };
};

export const diagnosticSourceFile = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  try {
    const file = new URL(value, 'https://diagnostic.invalid').pathname.split('/').pop() || '';
    return /^(?:main|\d+)\.[a-f0-9]{8,64}(?:\.chunk)?\.js$/.test(file) ? file : undefined;
  } catch { return undefined; }
};
